import { useEffect, useState, type CSSProperties } from 'react';
import type { AvatarId } from '@guess/protocol';
import type { GameActions } from '../app/connection';
import type { GameState } from '../app/state';
import { isHost, seatIndex } from '../app/state';
import { Avatar } from '../ui/Avatar';
import { AVATAR_IDS, AvatarGlyph } from '../ui/avatars';
import { Button } from '../ui/Button';
import { Seat } from '../ui/Seat';
import { cx } from '../ui/cx';
import { themeLabel } from '../ui/format';
import s from './screens.module.scss';

const COLOR_IDS = [1, 2, 3, 4, 5, 6, 7, 8] as const;

/** Envoi differe : pianoter sur le nombre de manches n'inonde pas la socket. */
const COMMIT_MS = 300;

/**
 * Le salon : qui joue, avec quels pouvoirs.
 *
 * C'est ici, et nulle part ailleurs, qu'on choisit son equipement. Le serveur
 * fige les pouvoirs au lancement (`chooseLoadout` avant la premiere manche),
 * donc l'ecran doit etre clair : apres, c'est trop tard.
 *
 * Les bots recoivent d'office les deux premiers pouvoirs du catalogue. Ne pas
 * proposer ce choix aux humains les ferait donc jouer desarmes contre une
 * table equipee — d'ou cet ecran, meme quand on joue seul contre des bots.
 */
export interface LobbyScreenProps {
  state: GameState;
  actions: GameActions;
}

export function LobbyScreen({ state, actions }: LobbyScreenProps) {
  const [picks, setPicks] = useState<string[]>([]);
  const [customizing, setCustomizing] = useState(false);
  const [roundsInput, setRoundsInput] = useState(state.totalRounds || 1);
  const host = isHost(state);
  const you = state.players.find((player) => player.id === state.you);
  const ready = state.you !== null && state.readyIds.includes(state.you);
  const humans = state.players.filter((player) => !player.bot).length;

  // Le serveur fait foi : s'il clampe (ou si les themes changent le pool),
  // le champ suit — meme principe que la mise sur l'ecran suivant.
  useEffect(() => {
    setRoundsInput(state.totalRounds || 1);
  }, [state.totalRounds]);

  useEffect(() => {
    if (!host || roundsInput === state.totalRounds) return undefined;
    const timer = window.setTimeout(() => actions.configure({ rounds: roundsInput }), COMMIT_MS);
    return () => window.clearTimeout(timer);
  }, [roundsInput, state.totalRounds, host, actions]);

  // Themes actifs, filtre vide compris : "rien de coche cote serveur" veut
  // dire "tout" a l'affichage, jamais une table vide de cases cochees.
  const activeThemes =
    state.selectedThemes.length > 0
      ? state.selectedThemes
      : state.availableThemes.map((theme) => theme.id);

  // Un equipement par defaut, pour que « Pret » suffise a lancer une partie.
  // Rester sans pouvoirs doit etre un choix, pas un oubli.
  useEffect(() => {
    if (picks.length === 0 && state.catalogue.length > 0) {
      const initial = state.catalogue.slice(0, state.loadoutSize).map((power) => power.id);
      setPicks(initial);
      actions.setLoadout(initial);
    }
  }, [state.catalogue, state.loadoutSize, picks.length, actions]);

  const toggle = (id: string) => {
    if (ready) return; // une fois pret, l'equipement ne bouge plus
    const next = picks.includes(id)
      ? picks.filter((pick) => pick !== id)
      : [...picks.slice(Math.max(0, picks.length - state.loadoutSize + 1)), id];
    setPicks(next);
    actions.setLoadout(next);
  };

  const invite = `${window.location.origin}/?r=${state.room ?? ''}`;

  return (
    <main className={s.lobby}>
      <section className={s.lobbyCode}>
        <span className={s.lobbyCodeLabel}>Code du salon</span>
        <strong className={s.lobbyCodeValue}>{state.room}</strong>
        <Button
          tone="ghost"
          onClick={() => {
            void navigator.clipboard?.writeText(invite);
          }}
        >
          Copier le lien
        </Button>
      </section>

      {host ? (
        <section className={s.lobbyPanel}>
          <h2 className={s.panelTitle}>Reglages de la partie</h2>

          <div className={s.settingsRow}>
            <span className={s.customizerLabel}>Themes</span>
            <div className={s.themeChips}>
              {state.availableThemes.map((theme) => {
                const checked = activeThemes.includes(theme.id);
                const lastOne = checked && activeThemes.length === 1;
                return (
                  <button
                    key={theme.id}
                    type="button"
                    className={cx(s.themeChip, checked && s.themeChipOn)}
                    disabled={lastOne}
                    aria-pressed={checked}
                    onClick={() => {
                      const all = state.availableThemes.map((t) => t.id);
                      const next = checked
                        ? activeThemes.filter((id) => id !== theme.id)
                        : [...activeThemes, theme.id];
                      // Tout redevient coche : c'est "aucun filtre", pas un
                      // filtre qui par coincidence couvre tout le catalogue.
                      actions.configure({ themes: next.length === all.length ? [] : next });
                    }}
                  >
                    {themeLabel(theme.id)}
                    <span className={s.themeChipCount}>{theme.count}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className={s.settingsRow}>
            <label className={s.customizerLabel} htmlFor="lobby-rounds">
              Manches
            </label>
            <input
              id="lobby-rounds"
              className={s.roundsInput}
              type="number"
              min={1}
              max={999}
              value={roundsInput}
              onChange={(event) => setRoundsInput(Math.max(1, Math.round(Number(event.target.value))))}
            />
          </div>

          <label className={s.settingsToggle}>
            <input
              type="checkbox"
              checked={state.powersEnabled}
              onChange={() => actions.configure({ powersEnabled: !state.powersEnabled })}
            />
            Pouvoirs (bonus / malus) actives
          </label>
        </section>
      ) : (
        <p className={s.lobbyNote}>
          {state.selectedThemes.length === 0
            ? 'Tous les themes'
            : state.selectedThemes.map(themeLabel).join(', ')}
          {' · '}
          {state.totalRounds} manche{state.totalRounds > 1 ? 's' : ''}
          {' · '}
          pouvoirs {state.powersEnabled ? 'actives' : 'coupes'}
        </p>
      )}

      <div className={s.lobbyColumns}>
        <section className={s.lobbyPanel}>
          <h2 className={s.panelTitle}>
            A table · {state.players.length} joueurs ({humans} humain{humans > 1 ? 's' : ''})
          </h2>

          <ul className={s.lobbySeats}>
            {state.players.map((player) => {
              const isYou = player.id === state.you;
              const playerReady = state.readyIds.includes(player.id);
              return (
                <li key={player.id} className={s.lobbySeatRow}>
                  {isYou ? (
                    <button
                      type="button"
                      className={s.lobbyAvatarButton}
                      onClick={() => setCustomizing((open) => !open)}
                      aria-expanded={customizing}
                      aria-label="Changer d'avatar et de couleur"
                    >
                      <Avatar
                        avatarId={player.avatarId}
                        colorId={player.colorId}
                        seatIndex={seatIndex(state, player.id)}
                      />
                    </button>
                  ) : (
                    <Avatar
                      avatarId={player.avatarId}
                      colorId={player.colorId}
                      seatIndex={seatIndex(state, player.id)}
                    />
                  )}

                  <Seat
                    name={player.name}
                    seatIndex={seatIndex(state, player.id)}
                    colorId={player.colorId}
                    bot={player.bot}
                    you={isYou}
                    offline={!player.connected}
                  />

                  {/* Vrai statut, vraie couleur : avant, ces points s'affichaient
                      en vert MEME pas pret — on ne peut pas s'y fier pour
                      savoir qui manque. */}
                  <span className={cx(s.lobbyReady, playerReady && s.lobbyReadyOn)}>
                    {playerReady ? 'pret' : 'en attente'}
                  </span>

                  {host && player.bot ? (
                    <Button tone="ghost" onClick={() => actions.removeBot(player.id)}>
                      retirer
                    </Button>
                  ) : null}
                </li>
              );
            })}
          </ul>

          {customizing && you ? (
            <div className={s.customizer}>
              <div className={s.customizerRow}>
                <span className={s.customizerLabel}>Avatar</span>
                <div className={s.customizerGlyphs}>
                  {AVATAR_IDS.map((avatarId) => (
                    <button
                      key={avatarId}
                      type="button"
                      className={cx(
                        s.customizerGlyph,
                        you.avatarId === avatarId && s.customizerGlyphPicked,
                      )}
                      onClick={() => actions.customize({ avatarId: avatarId as AvatarId })}
                      aria-label={avatarId}
                      aria-pressed={you.avatarId === avatarId}
                    >
                      <AvatarGlyph avatarId={avatarId} />
                    </button>
                  ))}
                </div>
              </div>

              <div className={s.customizerRow}>
                <span className={s.customizerLabel}>Couleur</span>
                <div className={s.customizerSwatches}>
                  {COLOR_IDS.map((colorId) => (
                    <button
                      key={colorId}
                      type="button"
                      className={cx(
                        s.customizerSwatch,
                        you.colorId === colorId && s.customizerSwatchPicked,
                      )}
                      style={{ '--seat': `var(--c-seat-${colorId})` } as CSSProperties}
                      onClick={() => actions.customize({ colorId })}
                      aria-label={`couleur ${colorId}`}
                      aria-pressed={you.colorId === colorId}
                    />
                  ))}
                </div>
              </div>
            </div>
          ) : null}

          {host ? (
            <div className={s.lobbyBots}>
              <span className={s.lobbyBotsLabel}>Ajouter un bot</span>
              <Button tone="ghost" onClick={() => actions.addBot('facile')}>
                facile
              </Button>
              <Button tone="ghost" onClick={() => actions.addBot('normal')}>
                normal
              </Button>
              <Button tone="ghost" onClick={() => actions.addBot('fort')}>
                fort
              </Button>
            </div>
          ) : (
            <p className={s.lobbyNote}>Seul l'hote peut ajouter des bots.</p>
          )}
        </section>

        <section className={s.lobbyPanel}>
          <h2 className={s.panelTitle}>
            Tes pouvoirs · {picks.length} / {state.loadoutSize}
          </h2>

          {!state.powersEnabled ? (
            <p className={s.lobbyNote}>L'hote a coupe les pouvoirs pour cette partie.</p>
          ) : (
            <>
              <p className={s.lobbyNote}>
                Ils se declarent AVANT chaque manche, avec la mise. Une fois la partie lancee, ce
                choix est fige.
              </p>

              <ul className={s.powerGrid}>
                {state.catalogue.map((power) => {
                  const picked = picks.includes(power.id);
                  return (
                    <li key={power.id}>
                      <button
                        type="button"
                        className={cx(s.powerCard, picked && s.powerCardPicked)}
                        onClick={() => toggle(power.id)}
                        disabled={ready}
                        aria-pressed={picked}
                      >
                        <span className={cx(s.powerKind, s[`kind-${power.kind}`])}>
                          {power.kind === 'bonus' ? 'bonus' : 'malus'}
                        </span>
                        <strong className={s.powerLabel}>{power.label}</strong>
                        <span className={s.powerDesc}>{power.description}</span>
                        <span className={s.powerMeta}>
                          {power.charges === null ? 'illimite' : `${power.charges} charges`}
                          {power.cooldownRounds > 0 ? ` · recharge ${power.cooldownRounds}` : ''}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </section>
      </div>

      <footer className={s.lobbyFooter}>
        <Button
          tone={ready ? 'ghost' : 'action'}
          size="lg"
          onClick={() => actions.setReady(!ready)}
          disabled={!you}
        >
          {ready ? 'Pas encore pret' : 'Pret'}
        </Button>
        <p className={s.lobbyNote}>
          La partie demarre des que TOUS les humains sont prets. Les bots le sont d'office.
        </p>
      </footer>
    </main>
  );
}
